import { Button } from "@/components/ui/button";
import { Carousel, type CarouselApi, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { useFirstVisitStore } from "@/store";
import { ThumbaIcon } from "@roman/shared/components/Icons";
import { cn } from "@roman/shared/utils";
import Autoplay from "embla-carousel-autoplay";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SkipModal } from "./SkipModal";

const IntroStep = () => {
  return (
    <div className="flex flex-col justify-between h-full pt-14 pb-24 h-xs:pb-28">
      <div className="flex gap-2.5 items-center w-full justify-center">
        <ThumbaIcon className="text-white scale-[125%]" />
        <img src="/assets/tutorial/thumba-text.png" alt="Thumba" width={136} />
      </div>

      <div className="w-full text-center text-white text-lg font-bold mt-5">“未来予想で、ちょい稼げる！”</div>

      <div className="w-full flex flex-1 min-h-0 items-center justify-center mt-5">
        <img src="/assets/tutorial/intro.png" alt="Intro" className="h-full w-auto object-contain" />
      </div>
    </div>
  );
};

const EarnPointsStep = () => {
  return (
    <div className="flex flex-col justify-between h-full pt-14 pb-24 h-xs:pb-28">
      <p className="text-center w-full justify-center text-white text-[30px] leading-[37px] font-extrabold">
        1. ポイントを獲得する
      </p>

      <div className="w-full flex flex-1 min-h-0 items-center justify-center mt-9">
        <img src="/assets/tutorial/earn-points-image.png" alt="Intro" className="h-full w-auto object-contain" />
      </div>

      <div className="w-full flex justify-center mt-5">
        <img
          src="/assets/tutorial/earn-points-guide.png"
          alt="Intro"
          className="h-[60px] h-xs:h-[81px] w-auto object-contain"
        />
      </div>
    </div>
  );
};

const PredictionMarketStep = () => {
  return (
    <div className="flex flex-col justify-between h-full pt-14 pb-24 h-xs:pb-28">
      <p className="text-center w-full justify-center text-white text-[30px] leading-[37px] font-extrabold">
        <span>2. 予測コンテンツで</span>
        <br />
        <span>ポイントを増やす</span>
      </p>

      <div className="mt-3 text-white text-sm font-bold">
        <p>「Yes / No」や選択肢に投票し、</p>
        <p>当たるとポイントがどんどん増える！</p>
        <p>みんなの投票で、倍率がリアルタイムに変動！</p>
      </div>

      <div className="w-full flex flex-1 justify-center mt-5 relative bg-gradient-to-t from-primary/85 to-transparent overflow-hidden">
        <img src="/assets/tutorial/prediction-market-phone.png" alt="Intro" className="h-full w-auto" />
        <div
          className="absolute bottom-0 left-0 w-full"
          style={{
            height: "10%",
            background: "linear-gradient(to top, #176BF2, transparent)",
          }}
        />
      </div>

      <div className="w-full flex justify-center mt-2">
        <img
          src="/assets/tutorial/prediction-market-guide.png"
          alt="Intro"
          className="h-[60px] h-xs:h-[81px] w-auto object-contain"
        />
      </div>
    </div>
  );
};

const RankAndRewardStep = () => {
  return (
    <div className="flex flex-col justify-between h-full pt-14 pb-24 h-xs:pb-28">
      <p className="text-center w-full justify-center text-white text-[30px] leading-[37px] font-extrabold">
        <span>3. ランクアップで</span>
        <br />
        <span>報酬ゲット!</span>
      </p>

      <div className="mt-3 text-white text-sm font-bold">
        <p>所持ポイント数に応じてランキング更新、</p>
        <p>上位を目指す楽しさ</p>
      </div>

      <div className="w-full flex flex-1 justify-center mt-5 relative bg-gradient-to-t from-primary/85 to-transparent overflow-hidden">
        <img src="/assets/tutorial/leaderboard-phone.png" alt="Intro" className="h-full w-auto" />
        <div
          className="absolute bottom-0 left-0 w-full"
          style={{
            height: "25%",
            background: "linear-gradient(to top, #176BF2, transparent)",
          }}
        />
      </div>

      <div className="w-full flex justify-center">
        <img src="/assets/tutorial/reward.png" alt="Intro" className="h-[72px] h-xs:h-[100px] w-auto object-contain" />
      </div>

      <div className="w-full flex justify-center mt-4">
        <img
          src="/assets/tutorial/reward-guide.png"
          alt="Intro"
          className="h-[60px] h-xs:h-[81px] w-auto object-contain"
        />
      </div>
    </div>
  );
};

const TemptationStep = () => {
  return (
    <div className="flex flex-col justify-between h-full pt-14 pb-24 h-xs:pb-28">
      <div className="flex gap-2.5 items-center w-full justify-center text-white">
        <span className="text-[30px] leading-[37px] font-bold">サンバの魅力</span>
        <img src="/assets/tutorial/temptation-3.png" alt="Thumba" className="w-[45px] md:w-[50px]" />
        <span className="text-[24px] font-medium">選</span>
      </div>

      <div className="flex flex-col h-1/2 min-h-0 gap-3 md:gap-5 px-2 justify-start items-center mt-10">
        <img src="/assets/tutorial/step-1.png" alt="step-1" className="flex-1 min-h-0 w-auto object-contain" />
        <img src="/assets/tutorial/step-2.png" alt="step-2" className="flex-1 min-h-0 w-auto object-contain" />
        <img src="/assets/tutorial/step-3.png" alt="step-3" className="flex-1 min-h-0 w-auto object-contain" />
      </div>

      <div className="flex flex-col flex-1 justify-between items-center text-white mt-10 mb-2">
        <img src="/assets/tutorial/dashes.png" alt="dashes" width={100} />

        <div className="text-[24px] sm:text-[26px] font-bold">始めよう！</div>

        <div className="text-md sm:text-lg font-bold">
          <p>あなたの予測が未来を作る！</p>
          <p>未来を予測して、</p>
          <p>ポイントを獲得しよう！</p>
        </div>
        <img src="/assets/tutorial/dashes.png" alt="dashes" width={100} />
      </div>
    </div>
  );
};

export const TutorialPage = () => {
  const navigate = useNavigate();
  const [api, setApi] = useState<CarouselApi>();
  const [currentStep, setCurrentStep] = useState(1);
  const [openSkipModal, setOpenSkipModal] = useState(false);

  const setHasVisitedPMVersion = useFirstVisitStore((state) => state.setHasVisitedPMVersion);

  const handleNext = () => {
    if (!api) {
      return;
    }

    if (api.canScrollNext()) {
      api.scrollNext();
      setCurrentStep(currentStep + 1);
    }

    if (currentStep === steps.length) {
      navigate("/login");
    }
  };

  const plugins = [Autoplay({ delay: 5000, stopOnInteraction: false })];

  useEffect(() => {
    if (!api) {
      return;
    }

    api.on("select", () => {
      setCurrentStep(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  useEffect(() => {
    setHasVisitedPMVersion(true);
  }, [setHasVisitedPMVersion]);

  const steps = [1, 2, 3, 4, 5];

  return (
    <div className="w-screen flex justify-center bg-white z-50 overflow-hidden h-dvh select-none">
      <div className="flex flex-col items-center justify-center max-w-md h-full relative bg-primary overflow-hidden]">
        <Carousel className={cn("w-full h-full z-10")} setApi={setApi} plugins={plugins}>
          <CarouselContent className="-ml-0 w-full">
            {steps.map((step) => (
              <CarouselItem className={"text-center -pl-0 relative h-dvh"} key={step}>
                {step === 1 && <IntroStep />}

                {step === 2 && <EarnPointsStep />}

                {step === 3 && <PredictionMarketStep />}

                {step === 4 && <RankAndRewardStep />}

                {step === 5 && <TemptationStep />}
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        <div className="flex justify-center items-center gap-2 absolute left-1/2 -translate-x-1/2 top-5 h:top-8">
          {steps.map((step, index) => (
            <div
              onClick={() => {
                setCurrentStep(index + 1);
                api?.scrollTo(index);
              }}
              key={step}
              className={cn(
                "cursor-pointer w-2 h-2 bg-white rounded-full opacity-50",
                index === currentStep - 1 && "opacity-100",
              )}
            />
          ))}
        </div>

        <Button
          onClick={handleNext}
          className="text-xl z-50 bg-white focus:bg-white hover:bg-white p-5 h-xs:p-6 px-12 text-primary rounded-full font-bold absolute bottom-7 h-xs:bottom-10 left-1/2 -translate-x-1/2 w-[134px]"
        >
          {currentStep === steps.length ? "始める" : "次へ"}
        </Button>

        {currentStep !== steps.length && (
          <button
            onClick={() => setOpenSkipModal(true)}
            type="button"
            className="text-sm text-white mt-5 font-bold absolute right-5 bottom-5 h-xs:right-7 h-xs:bottom-7 z-20"
          >
            SKIP
          </button>
        )}

        <ThumbaIcon className="absolute -left-3 bottom-7 scale-[1000%] text-white opacity-10 z-1" />
        <div
          className={cn(
            "text-[21px] bg-white text-primary absolute -left-[8.5rem] top-[1rem] font-extrabold py-3 -rotate-45 px-32 transition-all duration-500 z-10",
            currentStep === 1
              ? "opacity-100 translate-x-0 pointer-events-auto"
              : "opacity-0 -translate-x-32 pointer-events-none",
          )}
          style={{
            transitionProperty: "opacity, transform",
            transitionDuration: "500ms",
            transitionTimingFunction: "cubic-bezier(0.4, 0, 0.2, 1)",
          }}
        >
          RENEWAL
        </div>
        <SkipModal open={openSkipModal} setOpen={setOpenSkipModal} />
      </div>
    </div>
  );
};
